import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { URLUtils } from "abweb/utils/URLUtils";
import { $getEditor } from 'lexical';
import { $applyNodeReplacement, createEditor, DecoratorNode } from 'lexical';
import * as React from 'react';
import { Suspense } from 'react';
const ImageComponent = /*#__PURE__*/React.lazy(() => import('./ImageComponent'));
function convertImageElement(domNode) {
  const img = domNode;
  if (img.src.startsWith('file:///')) {
    return null;
  }
  const {
    alt: altText,
    src,
    width,
    height
  } = img;
  const resizable = true; // !src.startsWith("data:"); // embedded images are not resizable
  const node = $createImageNode({
    altText,
    height,
    src,
    width,
    resizable
  });
  // ABSDK-502: An image without width and height props has w=0,h=0. Update imageNode after the browser loads
  if (width === 0 || height === 0) {
    getNaturalWidthAndHeight(img, $getEditor(), node, {
      altText,
      src,
      resizable
    });
  }
  return {
    node
  };
}
function getNaturalWidthAndHeight(img, editor, node, nodeProps) {
  // To get the natural height and width, use the native Image constructor, set the onload callback, and THEN set the src.
  const ximg = new Image();
  ximg.onload = function () {
    editor.update(() => {
      const newImage = $createImageNode({
        ...nodeProps,
        width: ximg.naturalWidth,
        height: ximg.naturalHeight
      });
      node.replace(newImage);
    });
  };
  ximg.src = img.src;
}
export class ImageNode extends DecoratorNode {
  static getType() {
    return 'image';
  }
  static clone(node) {
    return new ImageNode(node.__src, node.__altText, node.__maxWidth, node.__width, node.__height, node.__showCaption, node.__caption, node.__captionsEnabled, node.__resizable, node.__key);
  }
  static importJSON(serializedNode) {
    const {
      altText,
      height,
      width,
      maxWidth,
      caption,
      src,
      showCaption
    } = serializedNode;
    const resizable = !src.startsWith("data:"); // embedded images are not resizable
    const node = $createImageNode({
      altText,
      height,
      maxWidth,
      showCaption,
      src,
      width,
      resizable
    });
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }
  exportDOM() {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    if (this.__width !== undefined) element.setAttribute('width', this.__width.toString());
    if (this.__height !== undefined) element.setAttribute('height', this.__height.toString());
    return {
      element
    };
  }
  static importDOM() {
    return {
      img: node => ({
        conversion: convertImageElement,
        priority: 0
      })
    };
  }
  constructor(src, altText, maxWidth, width, height, showCaption, caption, captionsEnabled, resizable, key) {
    super(key);
    _defineProperty(this, "__src", void 0);
    _defineProperty(this, "__altText", void 0);
    _defineProperty(this, "__width", void 0);
    _defineProperty(this, "__height", void 0);
    _defineProperty(this, "__maxWidth", void 0);
    _defineProperty(this, "__showCaption", void 0);
    _defineProperty(this, "__caption", void 0);
    // Captions cannot yet be used within editor cells
    _defineProperty(this, "__captionsEnabled", void 0);
    _defineProperty(this, "__resizable", void 0);
    this.__src = src;
    this.__altText = altText;
    this.__maxWidth = maxWidth;
    this.__width = width;
    this.__height = height;
    this.__showCaption = showCaption || false;
    this.__caption = caption || createEditor();
    this.__captionsEnabled = captionsEnabled || false;
    this.__resizable = resizable === undefined ? true : resizable;
  }
  exportJSON() {
    return {
      altText: this.getAltText(),
      caption: this.__caption.toJSON(),
      height: this.__height,
      maxWidth: this.__maxWidth,
      showCaption: this.__showCaption,
      src: this.getSrc(),
      type: 'image',
      version: 1,
      width: this.__width
    };
  }
  setWidthAndHeight(width, height) {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
    // update the src if it starts with data
    if (writable.__src.startsWith("data:") && width !== undefined && height !== undefined) {
      let src = writable.__src;
      // trim the old width and height
      src = src.replace(/width=[^;]*;/, "");
      src = src.replace(/height=[^;]*;/, "");
      // add the new width and height
      writable.__src = src.replace(/png;/, `png;width=${width};height=${height};`);
    }
    // update the src if it has the query parameters
    const questionMarkPos = writable.__src.indexOf("?");
    let queryStringParameters = {};
    if (questionMarkPos >= 0) {
      const queryString = writable.__src.substring(questionMarkPos + 1);
      queryStringParameters = URLUtils.queryStringToObject(queryString);
    }
    if (queryStringParameters["width"] && queryStringParameters["height"]) {
      const src = writable.__src.substring(0, questionMarkPos);
      const queryParams = "?width=" + width + "&height=" + height;
      writable.__src = src + queryParams;
    }
  }
  setShowCaption(showCaption) {
    const writable = this.getWritable();
    writable.__showCaption = showCaption;
  }

  // View

  createDOM(config) {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }
  updateDOM() {
    return false;
  }
  getSrc() {
    return this.__src;
  }
  getAltText() {
    return this.__altText;
  }
  getResizable() {
    return this.__resizable;
  }
  decorate() {
    return /*#__PURE__*/React.createElement(Suspense, {
      fallback: null
    }, /*#__PURE__*/React.createElement(ImageComponent, {
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
      height: this.__height,
      maxWidth: this.__maxWidth,
      nodeKey: this.getKey(),
      showCaption: this.__showCaption,
      caption: this.__caption,
      captionsEnabled: this.__captionsEnabled,
      resizable: this.__resizable
    }));
  }
}
export function $createImageNode({
  altText,
  height,
  maxWidth /* = 500 */,
  captionsEnabled,
  src,
  width,
  showCaption,
  caption,
  resizable,
  key
}) {
  return $applyNodeReplacement(new ImageNode(src, altText, maxWidth, width, height, showCaption, caption, captionsEnabled, resizable, key));
}
export function $isImageNode(node) {
  // eslint-disable-next-line abinitio/no-instanceof
  return node instanceof ImageNode;
}
//# sourceMappingURL=ImageNode.js.map
